import type { ReactNode } from 'react';

/**
 * Port of the DS `MetricCard` — the KPI tile: 12px uppercase label, 32px/800
 * Montserrat tabular value, delta + hint line (up #35784C, down #B23A2E).
 * Handoff copy rule: say what the number is, never how good it is.
 * KPI rows are `repeat(4, minmax(0,1fr))` with a 14px gap.
 */
export type DeltaTone = 'up' | 'down' | 'neutral';

const DELTA: Record<DeltaTone, string> = {
  up: 'text-[#35784C]',
  down: 'text-[#B23A2E]',
  neutral: 'text-muted',
};

export function MetricCard({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  hint,
  icon,
  accent = 'var(--admin-chart-1)',
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: DeltaTone;
  hint?: ReactNode;
  icon?: ReactNode;
  /** Icon-chip ground, from the --admin-chart-* sequence. */
  accent?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-app-card border border-panel-border bg-white p-5 font-body">
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs font-semibold uppercase tracking-[.14em] text-muted">{label}</span>
        {icon && (
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-app text-brown-deep"
            style={{ background: accent }}
          >
            {icon}
          </span>
        )}
      </div>
      <div className="font-heading text-[32px] font-extrabold leading-[1.1] tracking-[-.02em] text-brown-deep tabular-nums">
        {value}
      </div>
      {(delta || hint) && (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          {delta && <span className={`font-semibold ${DELTA[deltaTone]}`}>{delta}</span>}
          {hint && <span className="text-muted">{hint}</span>}
        </div>
      )}
    </div>
  );
}

/** Kit `Stat` — the inset cream-warm tile inside panels and dialogs (not the KPI tile). */
export function Stat({
  label,
  value,
  hint,
  action,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** A small link top-right, e.g. "See all" to where the number comes from. */
  action?: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-[10px] bg-cream-warm px-[13px] py-[11px] font-body">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[.14em] text-muted">{label}</span>
        {action}
      </div>
      <div className="mt-0.5 font-heading text-[19px] font-bold text-brown-deep tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

/** A KPI row: four tiles, `repeat(4, minmax(0,1fr))`, 14px gap (handoff). */
export function MetricRow({ children }: { children: ReactNode }) {
  // Two across on a phone (smallScreen.ts), four on desktop and tablet.
  return <div className="mb-3.5 grid grid-cols-2 gap-2.5 desk:grid-cols-4 desk:gap-3.5">{children}</div>;
}
